/**
 * Данные вошедшего пользователя, вход и выход.
 *
 * Здесь же хранится роль и зона ответственности — от них зависит,
 * что пользователю доступно в интерфейсе.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api, setToken, clearToken, getToken } from '../../api/client';

/**
 * Вход по логину и паролю.
 *
 * Сервер может ответить двумя способами: сразу выдать токен либо
 * потребовать подтверждение через Яндекс ID. Второй случай — не ошибка,
 * поэтому обрабатывается как обычный результат.
 */
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const data = await api.post('/api/login', { username, password });
      setToken(data.token);
      return { user: data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Восстановление сессии при открытии приложения.
 *
 * Если сохранённый токен просрочен, сервер ответит отказом — это штатная
 * ситуация, а не сбой: просто показываем экран входа.
 */
export const restoreSession = createAsyncThunk('auth/restore', async (_, { rejectWithValue }) => {
  if (!getToken()) return rejectWithValue(null);
  try {
    return await api.get('/api/me');
  } catch {
    clearToken();
    return rejectWithValue(null);
  }
});

const initialState = {
  user: null,
  /** loading — идёт проверка сохранённой сессии, интерфейс ещё не решён */
  status: 'loading',
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /** Завершение входа после подтверждения вторым фактором. */
    sessionEstablished(state, action) {
      setToken(action.payload.token);
      state.user = action.payload;
      state.status = 'authenticated';
      state.error = null;
    },
    logout(state) {
      clearToken();
      state.user = null;
      state.status = 'anonymous';
    },
    clearError(state) {
      state.error = null;
    },
    /** Сообщение о неудачном входе через поставщика. */
    authFailed(state, action) {
      state.status = 'anonymous';
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'submitting';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.status = 'authenticated';
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'anonymous';
        state.error = action.payload ?? 'Не удалось войти';
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.user = action.payload;
        state.status = 'authenticated';
      })
      .addCase(restoreSession.rejected, (state) => {
        state.status = 'anonymous';
      });
  },
});

export const { sessionEstablished, logout, clearError, authFailed } = authSlice.actions;
/**
 * Завершение входа через корпоративного поставщика.
 *
 * Поставщик возвращает браузер с токеном в адресе. Токен сразу
 * сохраняется, а адрес очищается: иначе он остался бы в истории
 * браузера и мог попасть в закладки.
 */
export function consumeAuthRedirect() {
  return (dispatch) => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('authToken');
    const error = params.get('auth');

    if (!token && !error) return;

    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch {
      /* в изолированных средах изменение адреса может быть недоступно */
    }

    if (token) {
      setToken(token);
      dispatch(restoreSession());
      return;
    }

    const messages = {
      error: 'Не удалось войти через корпоративную учётную запись.',
      expired: 'Время на вход истекло. Попробуйте ещё раз.',
    };
    dispatch(authFailed(messages[error] ?? messages.error));
  };
}

export default authSlice.reducer;

/* ── Выборки ───────────────────────────────────────────────────────────
   Проверки прав собраны здесь, а не разбросаны по компонентам: правило
   одно и то же во многих местах, и дублировать его — верный способ
   получить расхождение при изменении.

   Это подсказка интерфейсу, а не защита: настоящая проверка выполняется
   на сервере при каждом запросе. */

export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.status === 'authenticated';

export const selectIsDeveloper = (state) => state.auth.user?.role === 'developer';

/** Роли, которые ведут данные. Контролёр данных их не правит:
 *  иначе он проверял бы свои же изменения. */
const EDIT_ROLES = new Set(['admin', 'coordinator', 'developer']);
/** Участники отправки в NetBox — им доступен просмотр плана. */
const NETBOX_ROLES = new Set(['admin', 'coordinator', 'controller', 'developer']);

/** Право редактировать данные вообще. */
export const selectCanEdit = (state) => EDIT_ROLES.has(state.auth.user?.role);

/** Объект в зоне ответственности пользователя. */
export const selectInScope = (state, mag) => {
  const user = state.auth.user;
  if (!user) return false;
  const scope = user.scopeDivisions;
  if (!scope || scope.length === 0) return true;
  return state.divisions.magToDivision[mag] !== undefined
    ? scope.includes(state.divisions.magToDivision[mag])
    : false;
};

/** Окно «Отправка в NetBox» по объекту: участникам процесса в своей зоне. */
export const selectCanOpenNetbox = (state, mag) =>
  NETBOX_ROLES.has(state.auth.user?.role) && selectInScope(state, mag);

/**
 * Право редактировать конкретный объект.
 *
 * Помимо роли учитывается зона ответственности: пустой список означает
 * отсутствие ограничений, непустой — доступ только к своим дивизионам.
 */
export const selectCanEditObject = (state, mag) => {
  const user = state.auth.user;
  if (!user || !EDIT_ROLES.has(user.role)) return false;
  return selectInScope(state, mag);
};
